import { FeedFilter, PostMarker, RestUserFeed } from "../../schema-copy/api-schema";
import { UserPlan, getPlanLimits } from "../../schema-copy/userPlans";
import { isFilterActive, matchesFilter } from "../../schema-copy/feed-filter";
import { getPosts, POSTS_PER_FILE } from "../post-engine";

/**
 * Per-feed keyword filters: posts matching a user-configured filter are automatically marked as
 * read, so they never count against the unread badge. Everything runs client-side — post content
 * comes from the CDN post files, marking rides the normal read-marker sync. Pro/VIP only (see
 * PlanLimits.keywordFilters); the filters themselves live in UserPreferences.feedFilters so they
 * roam across devices.
 *
 * Unlike the auto-read limits (which are idempotent and re-enforced freely), a filter must
 * evaluate each post EXACTLY ONCE — otherwise a filtered post the user deliberately marked
 * unread again would be re-filtered on the next pass. So the engine keeps a per-feed
 * "evaluated up to index N" watermark, persisted per user in localStorage (shared across tabs).
 * On a device that first sees a filter (synced via preferences), the watermark starts at the
 * current post count: the device where the filter was configured already handled the backlog
 * (its markings arrive via marker sync), so no retroactive pass runs here.
 */

// Upper bound on posts fetched per watermark catch-up pass (10 CDN files) when new posts
// arrived. Bounds the catch-up cost after a long offline gap.
export const FILTER_SCAN_MAX = 200;
// Upper bound on CDN post files fetched per backlog pass (dialog preview / retroactive apply
// after an edit). The backlog pass scans the posts that are actually unread — which can sit far
// behind the newest post on high-volume feeds — so it reads the unread indices from the read
// markers and loads only the files containing them, newest files first.
export const FILTER_SCAN_MAX_FILES = 10;

export interface FilterEngineDeps {
  getFeed: (feedId: string) => RestUserFeed | undefined;
  isPostRead: (feedId: string, postIndex: number) => boolean;
  /** Ascending unread post indices within the marker engine's trackable window. */
  getUnreadIndices: (feedId: string) => number[];
  markPostsRead: (feedId: string, postIndices: PostMarker[]) => void;
  getUserPlan: () => UserPlan | undefined;
}

function watermarkStorageKey(userId: string) {
  return `feedFilterWatermarks-${userId}`;
}

export class FeedFilterEngine {
  private filters = new Map<string, FeedFilter>();
  /** Highest post-count basis already evaluated, per feed. */
  private watermarks: Record<string, number> = {};
  private userId: string | null = null;
  // Per-feed in-flight evaluation + a dirty flag so overlapping triggers (poll while a
  // catch-up pass still fetches) coalesce into one follow-up run instead of racing.
  private inFlight = new Map<string, Promise<void>>();
  private queued = new Set<string>();

  constructor(private deps: FilterEngineDeps) {}

  enable(userId: string): void {
    this.userId = userId;
    try {
      this.watermarks = JSON.parse(localStorage.getItem(watermarkStorageKey(userId)) ?? '{}');
    } catch (e) {
      console.error('Failed to load filter watermarks', e);
      this.watermarks = {};
    }
  }

  disable(): void {
    this.userId = null;
    this.filters.clear();
    this.watermarks = {};
    this.inFlight.clear();
    this.queued.clear();
  }

  private enabled(): boolean {
    const plan = this.deps.getUserPlan();
    return !!this.userId && !!plan && getPlanLimits(plan).keywordFilters;
  }

  /** (Re)seed the filters from the server preferences (initial load / reload) and catch up
   *  every filtered feed. Watermarks of feeds that no longer have a filter are dropped. */
  seedFilters(filters: Record<string, FeedFilter> | undefined): void {
    this.filters = new Map(Object.entries(filters ?? {}).filter(([, f]) => isFilterActive(f)));
    let dropped = false;
    for (const feedId of Object.keys(this.watermarks))
      if (!this.filters.has(feedId)) {
        delete this.watermarks[feedId];
        dropped = true;
      }
    if (dropped) this.saveWatermarks();
    if (!this.enabled()) return;
    this.filters.forEach((_, feedId) => this.evaluate(feedId));
  }

  getFilter(feedId: string): FeedFilter | undefined {
    return this.filters.get(feedId);
  }

  /** True when the feed has a usable filter (drives the "filtered" UI hints). */
  hasFilter(feedId: string): boolean {
    return this.filters.has(feedId);
  }

  /**
   * Sets/clears a feed's filter after a local user edit and runs the retroactive pass over the
   * currently unread posts (bounded by FILTER_SCAN_MAX_FILES) so the filter takes effect
   * immediately. The caller is responsible for persisting the filter to the user preferences.
   */
  async setFilter(feedId: string, filter: FeedFilter | undefined): Promise<void> {
    if (!isFilterActive(filter)) {
      this.filters.delete(feedId);
      delete this.watermarks[feedId];
      this.saveWatermarks();
      return;
    }
    this.filters.set(feedId, filter);
    if (!this.enabled()) return;
    await this.inFlight.get(feedId);
    const basis = this.feedBasis(feedId);
    await this.runUnread(feedId, filter)
      .catch(e => console.error(`[filter] retro pass failed for feed ${feedId}:`, e));
    this.setWatermark(feedId, basis);
  }

  /** Called when a poll reported new posts for the feed. */
  onFeedUpdated(feedId: string): void {
    if (this.filters.has(feedId) && this.enabled()) this.evaluate(feedId);
  }

  /**
   * Counts how many of the feed's currently unread posts (within FILTER_SCAN_MAX_FILES) the
   * given filter would mark read. Used by the filter dialog's live preview; fetched posts land
   * in the post cache, so the later retro pass costs no extra CDN requests.
   */
  async previewFilter(feedId: string, filter: FeedFilter | undefined): Promise<number> {
    if (!isFilterActive(filter)) return 0;
    const posts = await this.fetchUnreadPosts(feedId);
    return posts.filter(p => !this.deps.isPostRead(feedId, p.number) && matchesFilter(filter, p)).length;
  }

  /**
   * Fetches the feed's currently unread posts for a backlog pass (preview / retroactive apply).
   * The unread indices come from the read markers, so scattered old unread posts are found no
   * matter how far the feed has moved past them. Cost stays bounded: the indices are grouped
   * into their CDN post files and only the newest FILTER_SCAN_MAX_FILES files are fetched.
   */
  private async fetchUnreadPosts(feedId: string) {
    const unread = this.deps.getUnreadIndices(feedId);
    if (!unread.length) return [];
    // Post file start index -> unread indices in that file (ascending, since `unread` is).
    const files = new Map<number, number[]>();
    for (const i of unread) {
      const start = Math.floor(i / POSTS_PER_FILE) * POSTS_PER_FILE;
      const list = files.get(start);
      if (list) list.push(i);
      else files.set(start, [i]);
    }
    const starts = Array.from(files.keys()).sort((a, b) => b - a);
    if (starts.length > FILTER_SCAN_MAX_FILES)
      console.log(`[filter] feed ${feedId}: unread posts span ${starts.length} files, scanning newest ${FILTER_SCAN_MAX_FILES}`);
    const feed = this.deps.getFeed(feedId);
    const basis = this.feedBasis(feedId);
    const wanted = new Set(unread);
    const perFile = await Promise.all(starts.slice(0, FILTER_SCAN_MAX_FILES).map(start => {
      const indices = files.get(start)!;
      return getPosts({ id: feedId, postCount: basis, passphrase: feed?.passphrase, cdn: feed?.cdn, syncInstance: feed?.syncInstance },
        indices[0], indices[indices.length - 1] + 1);
    }));
    return perFile.flat().filter(p => wanted.has(p.number));
  }

  private feedBasis(feedId: string): number {
    const feed = this.deps.getFeed(feedId);
    return feed?.postCount ?? 0;
  }

  private evaluate(feedId: string): void {
    if (this.inFlight.has(feedId)) {
      this.queued.add(feedId);
      return;
    }
    const run = this.doEvaluate(feedId)
      .catch(e => console.error(`[filter] evaluation failed for feed ${feedId}:`, e))
      .finally(() => {
        this.inFlight.delete(feedId);
        if (this.queued.delete(feedId)) this.evaluate(feedId);
      });
    this.inFlight.set(feedId, run);
  }

  private async doEvaluate(feedId: string): Promise<void> {
    const filter = this.filters.get(feedId);
    if (!filter) return;
    const basis = this.feedBasis(feedId);
    if (!basis) return;
    const watermark = this.watermarks[feedId];
    if (watermark == undefined || watermark > basis) {
      // First sight of this filter on this device (or the feed's count shrank after a rebuild):
      // start tracking from here, the configuring device already handled the backlog.
      this.setWatermark(feedId, basis);
      return;
    }
    if (watermark >= basis) return;
    const start = Math.max(watermark, basis - FILTER_SCAN_MAX);
    if (start > watermark)
      console.log(`[filter] feed ${feedId}: catch-up capped, skipping posts ${watermark}-${start - 1}`);
    await this.runRange(feedId, filter, start, basis);
    // Only reached when the fetch succeeded — on failure the watermark stays, so the
    // next poll retries the same range.
    this.setWatermark(feedId, basis);
  }

  private async runRange(feedId: string, filter: FeedFilter, start: number, end: number): Promise<void> {
    const posts = await this.fetchRange(feedId, start, end);
    const matched: PostMarker[] = [];
    for (const post of posts)
      if (!this.deps.isPostRead(feedId, post.number) && matchesFilter(filter, post))
        matched.push(post.number);
    if (!matched.length) return;
    console.log(`[filter] feed ${feedId}: marking ${matched.length} of ${posts.length} posts read (indices ${start}-${end - 1})`);
    this.deps.markPostsRead(feedId, matched);
  }

  /** Retroactive pass after a filter edit: evaluates the currently unread posts (bounded by
   *  FILTER_SCAN_MAX_FILES) and marks the matches read. */
  private async runUnread(feedId: string, filter: FeedFilter): Promise<void> {
    const posts = await this.fetchUnreadPosts(feedId);
    const matched: PostMarker[] = [];
    for (const post of posts)
      if (!this.deps.isPostRead(feedId, post.number) && matchesFilter(filter, post))
        matched.push(post.number);
    if (!matched.length) return;
    console.log(`[filter] feed ${feedId}: marking ${matched.length} of ${posts.length} unread posts read`);
    this.deps.markPostsRead(feedId, matched);
  }

  private fetchRange(feedId: string, start: number, end: number) {
    const feed = this.deps.getFeed(feedId);
    return getPosts({ id: feedId, postCount: end, passphrase: feed?.passphrase, cdn: feed?.cdn, syncInstance: feed?.syncInstance }, start, end);
  }

  private setWatermark(feedId: string, basis: number): void {
    this.watermarks[feedId] = basis;
    this.saveWatermarks();
  }

  private saveWatermarks(): void {
    if (!this.userId) return;
    try {
      localStorage.setItem(watermarkStorageKey(this.userId), JSON.stringify(this.watermarks));
    } catch (e) {
      console.error('Failed to save filter watermarks', e);
    }
  }
}
